import { Link } from 'react-router-dom';
import HeroCarousel from './components/HeroCarousel';
import { usePageMeta } from '@/hooks/usePageMeta';
import CategoryQuickEntry from './components/CategoryQuickEntry';
import ContentCard from '@/components/base/ContentCard';
import SectionHeading from '@/components/base/SectionHeading';
import {
  formatDate,
  formatViews,
  getArticlesByCategory,
  getCategoryName,
  getHotArticles,
  getLatestArticles,
} from '@/utils/content';

const pickSections = ['tech', 'life', 'culture', 'entertainment'];

export default function Home() {
  usePageMeta(
    '鲁啊鲁 - 多元内容资讯与社区互动平台',
    '鲁啊鲁提供丰富的资讯内容，涵盖多领域话题，打造活跃互动社区，满足用户对品牌信息与多样内容的需求。',
  );
  const headline = getArticlesByCategory('current-affairs')[0];
  const headlineId = headline?.id;
  const newsList = getLatestArticles(8)
    .filter((item) => item.id !== headlineId)
    .slice(0, 4);
  const hotList = getHotArticles(6);

  return (
    <div className="w-full">
      <HeroCarousel />
      <CategoryQuickEntry />

      <div className="w-full px-4 md:px-8">
        {headline ? (
          <section className="pb-10 md:pb-12">
            <SectionHeading
              title="今日要闻"
              subtitle="聚焦时事与民生，记录正在发生的时代变化"
              icon="ri-flashlight-line"
              moreTo="/category/current-affairs"
            />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6">
              <Link
                to={`/detail/${headline.id}`}
                className="group lg:col-span-7 relative overflow-hidden rounded-lg bg-background-100"
              >
                <div className="w-full h-64 sm:h-80 lg:h-[420px]">
                  <img
                    src={headline.cover}
                    alt={`${headline.title} 配图`}
                    title={`${headline.title} 鲁啊鲁资讯`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                  <span className="inline-flex rounded-full bg-accent-500 px-2.5 py-0.5 text-xs font-medium text-background-50">
                    头条
                  </span>
                  <h3 className="mt-3 text-xl md:text-2xl font-bold leading-snug text-white line-clamp-2">
                    {headline.title}
                  </h3>
                  <p className="mt-2 hidden sm:block text-sm text-white/80 line-clamp-2">
                    {headline.summary}
                  </p>
                  <p className="mt-3 text-xs text-white/70">
                    {headline.author} · {formatDate(headline.date)} · {formatViews(headline.views)} 阅读
                  </p>
                </div>
              </Link>

              <div className="lg:col-span-5 flex flex-col gap-3">
                {newsList.map((article) => (
                  <ContentCard key={article.id} article={article} variant="horizontal" />
                ))}
              </div>
            </div>
          </section>
        ) : null}

        <section className="pb-10 md:pb-12">
          <SectionHeading
            title="热门榜单"
            subtitle="近期读者关注度最高的内容"
            icon="ri-bar-chart-box-line"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            {hotList.map((article, index) => (
              <Link
                key={article.id}
                to={`/detail/${article.id}`}
                className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-all hover:border-primary-300"
              >
                <span
                  className={`shrink-0 w-8 h-8 flex items-center justify-center rounded-md font-heading text-base font-bold ${
                    index < 3
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-background-100 text-foreground-400'
                  }`}
                >
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-700 transition-colors">
                    {article.title}
                  </h3>
                  <p className="mt-1 text-xs text-foreground-400">
                    {getCategoryName(article.category)} · {formatViews(article.views)} 阅读
                  </p>
                </div>
                <i className="ri-arrow-right-s-line text-foreground-300 group-hover:text-primary-500 transition-colors"></i>
              </Link>
            ))}
          </div>
        </section>

        {pickSections.map((key) => {
          const list = getArticlesByCategory(key);
          if (list.length === 0) return null;
          return (
            <section key={key} className="pb-10 md:pb-12">
              <SectionHeading
                title={`编辑精选 · ${getCategoryName(key)}`}
                subtitle="由编辑团队为你挑选的优质内容"
                icon="ri-sparkling-line"
                moreTo={`/category/${key}`}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {list.slice(0, 4).map((article) => (
                  <ContentCard key={article.id} article={article} />
                ))}
              </div>
            </section>
          );
        })}

        <section className="pb-14 md:pb-16">
          <div className="rounded-lg bg-primary-50 border border-primary-100 px-6 py-8 md:px-10 md:py-10">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
              <div className="lg:col-span-2">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  关于鲁啊鲁
                </h2>
                <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-600">
                  鲁啊鲁是一个面向广泛互联网用户的多元内容平台，聚合时事、科技、生活、文化与娱乐等领域资讯，
                  并通过社区让每一次阅读都延伸为交流。我们希望用清晰的内容结构，让信息更容易被看见、被理解。
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 transition-colors"
                  >
                    了解更多
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                  <Link
                    to="/community"
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 px-5 py-2.5 text-sm font-medium text-primary-700 hover:bg-primary-100 transition-colors"
                  >
                    加入社区
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-3 lg:grid-cols-1 gap-4">
                {[
                  { value: '5', label: '内容领域', icon: 'ri-layout-grid-line' },
                  { value: '20+', label: '精选文章', icon: 'ri-article-line' },
                  { value: '10+', label: '社区话题', icon: 'ri-chat-smile-2-line' },
                ].map((stat) => (
                  <div key={stat.label} className="flex items-center gap-3">
                    <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-accent-100 text-accent-700">
                      <i className={`${stat.icon} text-xl`}></i>
                    </span>
                    <span>
                      <span className="block font-heading text-lg font-bold text-foreground-950">{stat.value}</span>
                      <span className="block text-xs text-foreground-500">{stat.label}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}